import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WidgetChartConfig,
  WidgetChartType,
  MIN_BACKGROUND_UPDATE_INTERVAL_SECONDS,
  isBackgroundUpdateIntervalTooShort,
} from '../types';
import styles from './ConfigEditor.module.scss';

interface ConfigEditorProps {
  config: WidgetChartConfig;
  onChange: (config: WidgetChartConfig) => void;
  chartType: WidgetChartType;
}

export const ConfigEditor: React.FC<ConfigEditorProps> = ({
  config,
  onChange,
  chartType,
}) => {
  const { t } = useTranslation();
  const [jsonText, setJsonText] = useState(JSON.stringify(config, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);

  const handleChange = useCallback(
    (value: string) => {
      setJsonText(value);
      try {
        const parsed = JSON.parse(value);
        setParseError(null);
        onChange(parsed);
      } catch (e: any) {
        setParseError(e.message);
      }
    },
    [onChange]
  );

  const isChart = chartType === 'bar' || chartType === 'line' || chartType === 'pie';
  const isBarOrLine = chartType === 'bar' || chartType === 'line';
  const isMap = chartType === 'map';
  const isWordCloud = chartType === 'word_cloud';

  const clickVarSchema: Record<WidgetChartType, [string, string, string]> = {
    bar: [t('category (col 0)'), t('series name (col 1, 2 …)'), t('bar value')],
    line: [t('X-axis category (col 0)'), t('series name (col 1, 2 …)'), t('Y value')],
    pie: [t('slice name (col 0)'), t('slice name (col 0)'), t('slice value (col 1)')],
    table: [t('first-column cell value'), t('first-column name'), t('second-column cell value')],
    metric: [t('n/a'), t('n/a'), t('n/a')],
    card_grid: [t('n/a'), t('n/a'), t('n/a')],
    opinions: [t('n/a'), t('n/a'), t('n/a')],
    text: [t('n/a'), t('n/a'), t('n/a')],
    map: [t('country code (ISO-2) or region code (ISO 3166-2)'), t('country code (ISO-2) or region code (ISO 3166-2)'), t('numeric value (valueColumn)')],
    word_cloud: [t('token text (name column)'), t('token text (name column)'), t('token frequency (value column)')],
  };

  const currentClickVars = clickVarSchema[chartType];

  return (
    <div className={styles.container}>
      <textarea
        className={`${styles.textarea} ${parseError ? styles.hasError : ''}`}
        value={jsonText}
        onChange={(e) => handleChange(e.target.value)}
        spellCheck={false}
        rows={10}
      />
      {parseError && (
        <div className={styles.error}>
          {t('Invalid JSON')}: {parseError}
        </div>
      )}
      {!parseError && isBackgroundUpdateIntervalTooShort(config) && (
        <div className={styles.error}>
          {t('background_update refreshes at most once per hour: set cache_refresh_interval (or cache_ttl) to at least')} {MIN_BACKGROUND_UPDATE_INTERVAL_SECONDS}
        </div>
      )}
      <div className={styles.hint}>
        <p>{t('Available config options')}:</p>
        <ul>
          {isChart && (
            <li>
              <code>colors</code> - {t('array of hex color strings to override the default palette')}
              <br />
              <span style={{ fontSize: 11, color: '#666' }}>
                {t('Default')}: <code>["#3B83F6", "#06B6D5", "#A856F7", "#EC4899", "#12B981", "#84CC15", "#F59E0C", "#FA7315", "#F43F5F"]</code>
              </span>
            </li>
          )}
          {isChart && (
            <li>
              <code>useSentimentColors</code> - <code>true</code> {t('to color series/slices by sentiment category name')}
              <ul>
                <li><code>"POSITIVE"</code> → <span style={{ color: '#22C55F', fontWeight: 600 }}>#22C55F</span></li>
                <li><code>"NEUTRAL"</code> → <span style={{ color: '#ca9d00', fontWeight: 600 }}>#FFCC33</span></li>
                <li><code>"NEGATIVE"</code> → <span style={{ color: '#EF4444', fontWeight: 600 }}>#EF4444</span></li>
              </ul>
            </li>
          )}
          {isChart && (
            <li>
              <span style={{ fontSize: 11, color: '#555' }}>
                {t('Series or slices named')} <code>"Other"</code> {t('always receive color')} <span style={{ color: '#64758B', fontWeight: 600 }}>#64758B</span> {t('regardless of palette settings.')}
              </span>
            </li>
          )}
          {isChart && (
            <li><code>labels</code> - {t('object with')} {isBarOrLine ? 'x, y, title' : 'title'} {t('keys')}</li>
          )}
          {isChart && (
            <li><code>legend</code> - {t('object with position (top/bottom/left/right/none)')}</li>
          )}
          {isBarOrLine && (
            <li><code>showGrid</code> - {t('boolean to show/hide grid lines')}</li>
          )}
          {isBarOrLine && (
            <li>
              <code>stacked</code> - <code>true</code> {t('to stack multiple series into one bar/area per category (default: false = grouped/overlapping)')}
            </li>
          )}
          {isBarOrLine && (
            <li>
              <code>logScale</code> - <code>true</code> {t('to use a logarithmic value axis (useful when values span several orders of magnitude)')}
            </li>
          )}
          {chartType === 'bar' && (
            <li>
              <code>barDirection</code> - <code>"horizontal"</code> ({t('default')}) {t('or')} <code>"vertical"</code>
            </li>
          )}
          {chartType === 'bar' && (
            <li>
              <code>brushEnabled</code> - <code>true</code> {t('to enable brush selection and dataZoom toolbox for interactive range zooming')}
            </li>
          )}
          {chartType === 'bar' && (
            <li>
              <code>dataZoom</code> - <code>true</code> {t('to show a slider and inside dataZoom for panning and zooming when there are many categories (default: false)')}
            </li>
          )}
          {chartType === 'bar' && (
            <li>
              <code>axisBreak</code> - {t('enables axis break on the value axis. Optionally pre-define breaks:')} <code>{'{"initialBreaks": [{"start": 10, "end": 500}]}'}</code>. {t('Or leave empty and drag on the chart to draw breaks interactively.')}
            </li>
          )}
          {chartType === 'pie' && (
            <li><code>pieHole</code> - {t('number 0-1 for donut chart')}</li>
          )}
          {isWordCloud && (
            <>
              <li>
                <code>colors</code> - {t('first entry is the text color in frequency mode, where size alone shows frequency (default: blue); emojis keep their own colors')}
              </li>
              <li>
                <code>columnMapping</code> - <code>name</code> {t('token column')}, <code>value</code> {t('frequency column')} ({t('default: first and second column')})
              </li>
              <li>
                <code>wordCloudConfig</code> - {t('word cloud display settings')}
                <ul>
                  <li><code>mode</code> - <code>"keywords"</code>, <code>"emojis"</code> {t('or')} <code>"both"</code> ({t('default')})</li>
                  <li><code>showModeToggle</code> - <code>false</code> {t('hides the Keywords / Emojis / Both switch from viewers (default: shown when both kinds are present)')}</li>
                  <li><code>kindColumn</code> - {t('column holding')} <code>"keyword"</code> / <code>"emoji"</code> {t('per row; when omitted, emojis are detected from the token text')}</li>
                  <li><code>view</code> - <code>"cloud"</code> ({t('default')}) {t('or')} <code>"list"</code>; <code>showViewToggle</code> - <code>false</code> {t('hides the Cloud / List switch')}</li>
                  <li><code>allowHide</code> - <code>false</code> {t('stops viewers from hiding words (right-click in the cloud, × in the list)')}</li>
                  <li><code>trendColumn</code> - {t('column that says how much each word grew or shrank between two periods, as a fraction: 0.4 means 40% more mentions, -0.25 means 25% fewer, 0 means no change. When set, words can be colored by trend: blue = growing, red = shrinking, gray = about the same')}</li>
                  <li><code>sentimentColumn</code> - {t('column with the net sentiment per token (-1 to 1) or a positive / neutral / negative label; lets viewers color words by tone')}; <code>sentimentThreshold</code> - {t('score needed to count as positive or negative (default: 0.15)')}</li>
                  <li><code>colorBy</code> - <code>"frequency"</code>, <code>"trend"</code> {t('or')} <code>"sentiment"</code> {t('sets the starting color mode')}; <code>showColorToggle</code> - <code>false</code> {t('hides the Color selector')}; <code>trendThreshold</code> - {t('how big the change must be before a word counts as rising or falling; smaller changes show as steady (default: 0.3, i.e. 30%)')}</li>
                  <li><code>trendLabel</code> - {t('short text telling viewers which two periods were compared, e.g. "vs previous month"; shown after the legend and in the tooltip (default: "vs last week"). The built-in template compares the newer half of the fetched messages with the older half, so it uses "newer vs older half of the sample"')}</li>
                  <li><code>sampleColumn</code> - {t('column with the number of messages behind the counts, shown as a caption')}; <code>caption</code> - {t('custom caption text')}</li>
                  <li><code>maxWords</code> - {t('max tokens drawn, highest values win (default: 100)')}</li>
                  <li><code>minWordLength</code> - {t('drop keywords shorter than this (default: 2)')}</li>
                  <li><code>stopWords</code> - {t('array of keywords to hide, case-insensitive')}</li>
                  <li><code>sizeRange</code> - {t('font size range in px, e.g.')} <code>[12, 48]</code> ({t('default: fitted to the widget size')})</li>
                  <li><code>minEmojiSize</code> - {t('smallest emoji size in px (default: 45% of the top size, between 20 and 32)')}</li>
                  <li><code>shape</code> - <code>"circle"</code> ({t('default')}), <code>"cardioid"</code>, <code>"diamond"</code>, <code>"triangle-forward"</code>, <code>"triangle"</code>, <code>"pentagon"</code>, <code>"star"</code></li>
                  <li><code>rotate</code> - <code>true</code> {t('allows words to be rotated ±90°')}</li>
                  <li><code>gridSize</code> - {t('layout grid step in px, larger spaces words out (default: 8)')}</li>
                </ul>
                <pre style={{ fontSize: 10, marginTop: 6, padding: '6px 8px', background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflowX: 'auto' }}>{`"wordCloudConfig": {
  "mode": "both",
  "trendColumn": "trend",
  "sentimentColumn": "sentiment",
  "sampleColumn": "sample_size",
  "maxWords": 100,
  "stopWords": ["the", "and"]
}`}</pre>
              </li>
              <li><code>echartsOption</code> - {t('raw ECharts option object, deep-merged into the chart config')}</li>
            </>
          )}
          {chartType === 'table' && (
            <>
              <li>
                <code>pageSize</code> - {t('enables server-side pagination — number of rows fetched per page (requires')} <code>{'LIMIT {{__limit__}} OFFSET {{__offset__}}'}</code> {t('in SQL)')}
              </li>
              <li>
                <code>tableConfig</code> - {t('table display settings')}
                <ul>
                  <li>
                    <code>freezeColumnIndex</code> - {t('freeze columns up to this 1-based index (e.g.')} <code>1</code> {t('= first column sticky, omit or')} <code>0</code> {t('= no freeze)')}
                  </li>
                  <li>
                    <code>freezeHeader</code> - {t('keep header row visible while scrolling (default:')} <code>true</code>)
                  </li>
                  <li>
                    <code>freezeFooter</code> - {t('keep footer row visible while scrolling (default:')} <code>false</code>)
                  </li>
                  <li>
                    <code>showFooter</code> - {t('show aggregate footer row — sums numeric columns, shows row count for first column (default:')} <code>false</code>)
                  </li>
                  <li>
                    <code>pageSize</code> - {t('rows per page for client-side pagination (default:')} <code>10</code>). {t('Ignored when top-level')} <code>pageSize</code> {t('(server-side) is set.')}
                  </li>
                  <li>
                    <code>columnColors</code> - {t('map of 1-based column index to hex color for column highlighting, e.g.')} <code>{'{"1":"#3B83F6","3":"#F43F5F"}'}</code>
                  </li>
                  <li>
                    <code>columnOrder</code> - {t('array of column names in display order, e.g.')} <code>{'["status","name","date"]'}</code>. {t('Columns not listed appear after in their original order.')}
                  </li>
                  <li>
                    <code>columnWidths</code> - {t('initial column widths as % of table width by column name, e.g.')} <code>{'{"id":5,"url":8,"description":40}'}</code>. {t('Users can also drag header borders to resize at runtime.')}
                  </li>
                </ul>
                <pre style={{ fontSize: 10, marginTop: 6, padding: '6px 8px', background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflowX: 'auto' }}>{`"tableConfig": {
  "freezeColumnIndex": 1,
  "freezeHeader": true,
  "freezeFooter": false,
  "showFooter": true,
  "pageSize": 25,
  "columnColors": { "2": "#3B83F6", "4": "#F43F5F" },
  "columnOrder": ["status", "name", "date"],
  "columnWidths": { "id": 5, "status": 10, "description": 40 }
}`}</pre>
              </li>
            </>
          )}
          {chartType === 'card_grid' && (
            <>
              <li>
                <code>cardGridConfig</code> - {t('card grid display settings')}
                <ul>
                  <li><code>dateCol</code> - {t('column for the card date label (auto-detected from column name when omitted)')}</li>
                  <li><code>dateEndCol</code> - {t('optional end-date column — renders a date range "dateCol — dateEndCol"')}</li>
                  <li><code>titleCol</code> - {t('column for the card title (auto-detected from column name when omitted)')}</li>
                  <li><code>descriptionCol</code> - {t('column for the card body text (auto-detected from column name when omitted)')}</li>
                  <li><code>sparklineCol</code> - {t('column containing a JSON array string, e.g. "[52,49,94,70]", for the per-card sparkline (auto-detected when omitted)')}</li>
                  <li><code>sparklineLabel</code> - {t('label shown in the sparkline tooltip, e.g. "Messages" (defaults to column name)')}</li>
                  <li><code>sparklineColor</code> - {t('hex color for the sparkline line (default: "#3B83F6")')}</li>
                  <li><code>showDescription</code> - {t('show/hide the description text on each card (default: true)')}</li>
                  <li><code>showMetrics</code> - {t('show/hide the metrics row on each card (default: true)')}</li>
                  <li>
                    <code>clickUrlTemplate</code> - {t('URL opened when a card is clicked — use {column_name} placeholders:')}
                    <ul>
                      <li><strong>{t('Inner (app page)')}</strong>: <code>/threats/{'{id}'}</code> — {t('relative path, column values are URL-encoded')}</li>
                      <li><strong>{t('Outer (external link)')}</strong>: <code>{'{url}'}</code> — {t('when the column already contains a full URL (http/https), it is used as-is without encoding')}</li>
                    </ul>
                  </li>
                  <li><code>clickTarget</code> - <code>"_blank"</code> ({t('default')}) {t('or')} <code>"_self"</code></li>
                  <li><code>topicsCol</code> - {t('column for topic badges — JSON array string or comma-separated values')}</li>
                  <li><code>tagsCol</code> - {t('column for tag badges — JSON array string or comma-separated values')}</li>
                  <li><code>showBadges</code> - {t('show/hide the badges row (default: true when topicsCol or tagsCol is set)')}</li>
                  <li>
                    <code>sentimentCol</code> - {t('column containing a sentiment label — displayed as a colored metric value alongside other metrics. Accepted values (case-insensitive):')}
                    <ul>
                      <li><code>positive</code>, <code>negative</code>, <code>neutral</code></li>
                      <li><code>mostly_positive</code>, <code>mostly_negative</code>, <code>controversial</code></li>
                    </ul>
                    <span style={{ fontSize: 11, color: '#555' }}>
                      {t('For topic events: use')} <code>opinion_balance&apos;&gt;&gt;&apos;sentiment&apos;</code> {t('in SQL (value is pre-computed).')}
                      {' '}{t('For narratives: write a CASE expression against narrative_sentiment_widget shares.')}
                      {' '}{t('Unknown values are silently ignored.')}
                    </span>
                  </li>
                  <li><code>cardsPerPage</code> - {t('number of cards shown per page (default: 9). Increase the SQL')} <code>LIMIT</code> {t('to match, otherwise extra pages will be empty.')}</li>
                  <li><code>sentimentBackground</code> - {t('tint each card background with a washed-out color matching its sentiment — requires sentimentCol (default: false)')}</li>
                  <li><code>cardBackground</code> - {t('CSS background color applied to every card, e.g. "#FEF9C3" or "rgba(59,131,246,0.08)" — low-alpha rgba values stay readable in the dark theme')}</li>
                  <li>
                    <code>cardBackgroundCol</code> - {t('column whose value is a CSS color used as that card’s background — rows with an empty value fall back to cardBackground. Produce via a SQL CASE expression:')}
                    <pre style={{ fontSize: 10, marginTop: 4, padding: '4px 8px', background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflowX: 'auto' }}>{`CASE
  WHEN severity = 'high' THEN '#FEE2E2'
  WHEN severity = 'medium' THEN '#FEF9C3'
END AS card_color`}</pre>
                  </li>
                  <li>
                    <code>metrics</code> - {t('array of metric definitions shown at the bottom of each card')}
                    <ul>
                      <li><code>col</code> - {t('column name')}</li>
                      <li><code>label</code> - {t('display label shown above the value')}</li>
                      <li><code>format</code> - <code>\"compact\"</code> ({t('default, e.g. 1.2K')}) {t('or')} <code>\"number\"</code> ({t('raw number')})</li>
                    </ul>
                  </li>
                </ul>
                <pre style={{ fontSize: 10, marginTop: 6, padding: '6px 8px', background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflowX: 'auto' }}>{`"cardGridConfig": {
  "dateCol": "start_date",
  "dateEndCol": "end_date",
  "titleCol": "title",
  "descriptionCol": "description",
  "sparklineCol": "sparkline_data",
  "sparklineColor": "#3B83F6",
  "showDescription": true,
  "showMetrics": true,
  "topicsCol": "topics",
  "tagsCol": "tags",
  "showBadges": true,
  "sentimentCol": "sentiment",
  "sentimentBackground": true,
  "cardBackground": "rgba(59,131,246,0.08)",
  "cardBackgroundCol": "card_color",
  "cardsPerPage": 9,
  "clickUrlTemplate": "/threats/{id}",
  "clickTarget": "_blank",
  "metrics": [
    { "col": "publications", "label": "Pubs", "format": "compact" },
    { "col": "views",        "label": "Views", "format": "compact" },
    { "col": "actors",       "label": "Actors", "format": "compact" }
  ]
}`}</pre>
              </li>
            </>
          )}
          {chartType === 'opinions' && (
            <>
              <li>
                <code>opinionsConfig</code> - {t('opinions grid display settings')}
                <ul>
                  <li><code>titleCol</code> - {t('column for the card title (auto-detected from column name when omitted)')}</li>
                  <li>
                    <code>sentimentCol</code> - {t('column with the sentiment value (case-insensitive; auto-detected from column name when omitted).')}
                    <ul>
                      <li>{t('Sentiment icon: uses')} <code>"positive"</code>, <code>"negative"</code>, {t('or falls back to the neutral icon for any other value.')}</li>
                      <li>{t('sentimentBackground tint (below): supports all six normalized values —')} <code>positive</code>, <code>negative</code>, <code>neutral</code>, <code>mostly_positive</code>, <code>mostly_negative</code>, <code>controversial</code>.</li>
                    </ul>
                  </li>
                  <li><code>showMetrics</code> - {t('show/hide the metrics row on each card (default: true)')}</li>
                  <li>
                    <code>clickUrlTemplate</code> - {t('URL opened when a card is clicked — use {column_name} placeholders:')}
                    <ul>
                      <li><strong>{t('Inner (app page)')}</strong>: <code>/topic_reports/{'{narrative_id}'}/events/{'{event_id}'}</code> — {t('relative path, column values are URL-encoded')}</li>
                      <li><strong>{t('Outer (external link)')}</strong>: <code>{'{url}'}</code> — {t('when the column already contains a full URL (http/https), it is used as-is without encoding')}</li>
                    </ul>
                  </li>
                  <li><code>clickTarget</code> - <code>"_blank"</code> ({t('default')}) {t('or')} <code>"_self"</code></li>
                  <li><code>cardsPerPage</code> - {t('number of cards shown per page (default: 9). Increase the SQL')} <code>LIMIT</code> {t('to match, otherwise extra pages will be empty.')}</li>
                  <li><code>sentimentBackground</code> - {t('tint each card background with a washed-out color matching its sentiment — requires sentimentCol (default: false)')}</li>
                  <li>
                    <code>metrics</code> - {t('array of metric definitions shown at the bottom of each card')}
                    <ul>
                      <li><code>col</code> - {t('column name')}</li>
                      <li><code>label</code> - {t('display label shown before the value, e.g. "Rating"')}</li>
                      <li><code>format</code> - <code>"compact"</code> ({t('default, e.g. 1.2K')}) {t('or')} <code>"number"</code> ({t('raw number')})</li>
                      <li><code>tooltip</code> - {t('optional hint text shown via an info icon next to the metric, e.g. explaining how a rating is calculated')}</li>
                    </ul>
                  </li>
                </ul>
                <pre style={{ fontSize: 10, marginTop: 6, padding: '6px 8px', background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflowX: 'auto' }}>{`"opinionsConfig": {
  "titleCol": "title",
  "sentimentCol": "sentiment",
  "sentimentBackground": true,
  "cardsPerPage": 9,
  "showMetrics": true,
  "clickUrlTemplate": "/topic_reports/{narrative_id}/events/{event_id}",
  "clickTarget": "_blank",
  "metrics": [
    { "col": "rating",       "label": "Rating",       "format": "compact", "tooltip": "Rating based on the number of reactions and comments" },
    { "col": "publications", "label": "Publications", "format": "compact" },
    { "col": "actors",       "label": "Actors",       "format": "compact" }
  ]
}`}</pre>
              </li>
            </>
          )}
          {isMap && (
            <>
              <li>
                <code>mapConfig</code> - {t('map display settings')}
                <ul>
                  <li>
                    <code>mapType</code> - <code>"world"</code> {t('(default)')}, <code>"ukraine"</code>, <code>"germany"</code> {t('or')} <code>"russia"</code>.{' '}
                    {t('Regional maps show that country’s regions with neighbor countries as a gray backdrop')}
                  </li>
                  <li>
                    <code>federalLevel</code> - {t('Russia map only:')} <code>"subjects"</code> {t('(default, 83 federal subjects) or')} <code>"districts"</code> {t('(8 federal districts joined by RU-CFO-style codes)')}
                  </li>
                  <li>
                    <code>showKaliningrad</code> - {t('Russia map only: include the Kaliningrad exclave (default:')} <code>true</code>)
                  </li>
                  <li>
                    <code>countryColumn</code> - {t('column with ISO 3166-1 alpha-2 country codes for the world map, or ISO 3166-2 region codes for regional maps (defaults to first column)')}
                    <br />
                    <span style={{ fontSize: 11, color: '#666' }}>
                      {t('Example values')}: <code>"US"</code>, <code>"DE"</code>, <code>"UA"</code> ({t('world')}); <code>"UA-63"</code>, <code>"DE-BY"</code>, <code>"RU-MOW"</code> ({t('regional')}); <code>"RU-SZFO"</code> ({t('federal districts')})
                    </span>
                  </li>
                  <li><code>showCities</code> - {t('city-name markers on regional maps (default:')} <code>true</code>; {t('defaults to')} <code>false</code> {t('when labelColumn is set')})</li>
                  <li>
                    <code>labelColumn</code> - {t('column whose raw value is drawn on each region or country (e.g. "5.8%", a number, any string); shapes without data get no label. Works on all map types. On regional maps hides city markers unless showCities is explicitly true')}
                  </li>
                  <li>
                    <code>sentimentColumn</code> - {t('column with sentiment values (positive, negative, neutral, mostly_positive, mostly_negative, controversial); fills regions or countries with sentiment colors instead of the choropleth gradient. Works on all map types')}
                  </li>
                  <li>
                    <code>showSentimentLegend</code> - {t('show the sentiment color legend when sentimentColumn is set (default:')} <code>true</code>)
                  </li>
                  <li><code>seaColor</code> - {t('background (sea) color on regional maps (default: light blue-gray)')}</li>
                  <li><code>regionMapping</code> - {t('data value → code translation applied before the join, e.g.')} <code>{'{"Kharkiv branch": "UA-63"}'}</code>; {t('rows mapped to the same code have numeric columns summed')}</li>
                  <li><code>valueColumn</code> - {t('numeric column for choropleth fill intensity; omit for a presence map (all data countries share one color)')}</li>
                  <li><code>tooltipColumns</code> - {t('array of columns shown in the hover tooltip, in order (defaults to all non-country columns)')}</li>
                  <li><code>tooltipLabels</code> - {t('display label overrides per column, e.g.')} <code>{'{"pub_count": "Publications"}'}</code></li>
                  <li><code>colorRange</code> - {t('two-stop gradient for choropleth: [low-color, high-color] (default: light-to-dark blue)')}</li>
                  <li><code>emptyColor</code> - {t('fill color for countries with no data (default: light gray)')}</li>
                  <li><code>roam</code> - {t('enable scroll-wheel zoom and drag-to-pan (default:')} <code>true</code>)</li>
                  <li><code>showRank</code> - {t('show rank (#N of M) in the hover tooltip when valueColumn is set (default:')} <code>false</code>)</li>
                  <li>
                    <code>center</code> - {t('initial map center as [longitude, latitude] (default: world view)')}
                    <br />
                    <span style={{ fontSize: 11, color: '#666' }}>
                      {t('Examples')}: <code>[15, 52]</code> {t('Europe')}, <code>[30, 15]</code> {t('Africa')}, <code>[-95, 38]</code> {t('USA')}, <code>[90, 35]</code> {t('Asia')}
                    </span>
                  </li>
                  <li><code>zoom</code> - {t('initial zoom level (1 = world, 3 = continent, 6 = country). Requires')} <code>roam: true</code> {t('to let users zoom further.')}</li>
                </ul>
                <pre style={{ fontSize: 10, marginTop: 6, padding: '6px 8px', background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflowX: 'auto' }}>{`"mapConfig": {
  "countryColumn": "country_code",
  "valueColumn": "pub_count",
  "tooltipColumns": ["pub_count", "actor_count"],
  "tooltipLabels": { "pub_count": "Publications", "actor_count": "Actors" },
  "colorRange": ["#dbeafe", "#1d4ed8"],
  "emptyColor": "#e5e7eb",
  "roam": true,
  "center": [15, 52],
  "zoom": 3
}`}</pre>
              </li>
              <li>
                <code>echartsOption</code> - {t('raw ECharts option object, deep-merged into the chart config (see')}{' '}
                <a href="https://echarts.apache.org/en/option.html" target="_blank" rel="noopener noreferrer">
                  {t('ECharts docs')}
                </a>)
              </li>
            </>
          )}
          {chartType === 'metric' && (
            <>
              <li>
                <code>metricConfig</code> - {t('metric card display settings')}
                <ul>
                  <li><code>trendRange</code> - {t('number of days for the trend window (default:')} <code>7</code>). {t('Sync with SQL via')} <code>{'{{trend_days}}'}</code> {t('in defaultValues.')}</li>
                  <li><code>trendLabel</code> - {t('override the trend period label (default: "last {trendRange} days")')}</li>
                  <li><code>mainValueMode</code> - <code>"last"</code> ({t('default')}), <code>"sum"</code>, {t('or')} <code>"avg"</code> — {t('how to aggregate time-series into the headline number')}</li>
                  <li><code>sparklineColor</code> - {t('hex color for the sparkline line (default: "#3B83F6")')}</li>
                  <li><code>showSparkline</code> - {t('force show/hide sparkline; auto-detected when omitted')}</li>
                  <li><code>valuePrefix</code> / <code>valueSuffix</code> - {t('text prepended/appended to the main value (e.g. "$" or "%")')}</li>
                </ul>
              </li>
              <li>
                <code>columnMapping</code> - {t('metric column roles')}
                <ul>
                  <li><code>mainValue</code> - {t('column containing the primary numeric value (auto-detected when omitted)')}</li>
                  <li><code>trendPercent</code> - {t('column with a pre-computed trend % (overrides auto-computation from time series)')}</li>
                  <li><code>xAxis</code> - {t('date column for the sparkline x-axis (auto-detected when omitted)')}</li>
                </ul>
              </li>
              <li>
                <p style={{ marginTop: 4, fontSize: 11 }}>
                  {t('SQL tip: return a time series (date + value columns) for sparkline + auto trend, or a single row for a plain value card. Use')} <code>{'{{trend_days}}'}</code> {t('as a SQL parameter and set')} <code>defaultValues.trend_days</code> {t('to match metricConfig.trendRange.')}
                </p>
              </li>
            </>
          )}
          {isChart && (
            <li>
              <code>columnMapping</code> - {t('maps query columns to chart roles')}
              <ul>
                {isBarOrLine && (
                  <>
                    <li><code>xAxis</code> - {t('X-axis column')}</li>
                    <li><code>yAxis</code> - {t('Y-axis value column')}</li>
                    <li><code>key</code> - {t('grouping column — each unique value becomes a separate series')}</li>
                    <li><code>label</code> - {t('display name column for series (falls back to key value)')}</li>
                  </>
                )}
                {chartType === 'pie' && (
                  <>
                    <li><code>name</code> - {t('slice name column')}</li>
                    <li><code>value</code> - {t('slice value column')}</li>
                  </>
                )}
                <li>
                  <p style={{ marginTop: 4, fontSize: 11 }}>
                    * {t('When omitted, columns are mapped positionally: columns[0] = x/name, columns[1] = y/value')}
                  </p>
                </li>
              </ul>
            </li>
          )}
          {isChart && (
            <li><code>echartsOption</code> - {t('raw ECharts option object, deep-merged into the chart config (see')}{' '}
              <a href="https://echarts.apache.org/en/option.html" target="_blank" rel="noopener noreferrer">
                {t('ECharts docs')}
              </a>)
            </li>
          )}
          {chartType === 'text' && (
            <li>
              <code>textConfig</code> - {t('text widget display settings')}
              <ul>
                <li><code>backgroundColor</code> - {t('background color (any CSS value, e.g.')} <code>"#f9fafb"</code>). {t('Default: transparent.')}</li>
                <li><code>textColor</code> - {t('text color (any CSS value, e.g.')} <code>"#111827"</code>). {t('Default: inherited from theme.')}</li>
              </ul>
              <pre style={{ fontSize: 10, marginTop: 6, padding: '6px 8px', background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflowX: 'auto' }}>{`"textConfig": {
  "backgroundColor": "#f9fafb",
  "textColor": "#111827"
}`}</pre>
            </li>
          )}
          <li style={{ listStyle: 'none', marginTop: 14, marginLeft: -4, paddingTop: 10, borderTop: '1px solid #e5e7eb', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
            {t('General config')}
          </li>
          <li>
            <code>cache_ttl</code> - {t('cache duration in seconds (0 or absent = no cache)')}
          </li>
          <li>
            <code>cache_type</code> - <code>"blocking"</code> ({t('default')}), <code>"stale_while_revalidate"</code> ({t('serve stale data instantly, refresh in background')}), {t('or')} <code>"background_update"</code> ({t('auto-refresh on a timer, even without user requests')})
          </li>
          <li>
            <code>cache_refresh_interval</code> - {t('seconds between automatic refreshes (only for')} <code>"background_update"</code>{t(', defaults to')} <code>cache_ttl</code>; {t('minimum')} <code>{MIN_BACKGROUND_UPDATE_INTERVAL_SECONDS}</code>)
          </li>
          {(isChart || chartType === 'table' || isMap || isWordCloud) && (
          <li>
            <code>clickAction</code> - {t('action triggered when a chart element, map feature, or table row is clicked')}
            {chartType === 'table' && (
              <p style={{ marginTop: 4, marginBottom: 2, fontSize: 11, color: '#6b7280' }}>
                💡 {t('For tables, if you just need clickable links, include a URL column in your SQL and the table will render it as a link automatically — no')} <code>clickAction</code> {t('needed.')} {t('Use')} <code>clickAction</code> {t('only when you need row-click navigation based on a non-URL column value, or to drive a filter.')}<br/>
                ⚠️ {t('Table click variables ({key}, {label}, {value}) map to first/second column values, which may be unexpected — check the variables table below before configuring.')}
              </p>
            )}
            <ul>
              <li><code>type</code> - <code>"url"</code> {t('or')} <code>"set_filter"</code></li>
              <li>
                <p style={{ marginTop: 4, marginBottom: 4 }}><strong>{t('type "url"')}:</strong></p>
                <code>url</code> - {t('URL template with')} <code>{'{key}'}</code>, <code>{'{label}'}</code>, <code>{'{value}'}</code> {t('placeholders (URL-encoded automatically)')}
                <br />
                <code>target</code> - <code>"_blank"</code> ({t('default')}) {t('or')} <code>"_self"</code>
              </li>
              <li>
                <p style={{ marginTop: 4, marginBottom: 4 }}><strong>{t('type "set_filter"')}:</strong> {t('sets a dashboard filter value when clicked')}</p>
                <code>parameterName</code> - {t('the filter parameter name to set (must match a dashboard filter)')}<br />
                <code>valueTemplate</code> - {t('value template, e.g.')} <code>{'{key}'}</code> ({t('default')}), <code>{'{value}'}</code>
              </li>
              <li>
                <p style={{ marginTop: 6, marginBottom: 2 }}><strong>{t('Click variables for this chart type')}:</strong></p>
                <table style={{ fontSize: 11, borderCollapse: 'collapse' as const }}>
                  <thead>
                    <tr>
                      {['{key}', '{label}', '{value}'].map((h) => (
                        <th key={h} style={{ padding: '2px 8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}><code>{h}</code></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '2px 8px', color: '#555' }}>{currentClickVars[0]}</td>
                      <td style={{ padding: '2px 8px', color: '#555' }}>{currentClickVars[1]}</td>
                      <td style={{ padding: '2px 8px', color: '#555' }}>{currentClickVars[2]}</td>
                    </tr>
                  </tbody>
                </table>
              </li>
            </ul>
          </li>
          )}
        </ul>
      </div>
    </div>
  );
};
